import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { ItemData } from "types/item.api";
import {
  addItem,
  addItemFromCSV,
  deleteItem,
  editItem,
  getItems,
} from "./thunk";
import { DepositType } from "Components/Quotes/ManageQuotes/AddDepositModal";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface ItemStatus {
  fetch: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
}

interface ItemErrors {
  fetch: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
}
interface Totals {
  subTotal: number;
  totalTax: number;
  totalDiscount: number;
  totalDeposit: number;
  grandTotal: number;
  depositType: DepositType | null;
}

export interface CategoryState {
  items: ItemData[];
  addItems: ItemData[];
  ids: number[];
  pagination: PaginationMeta;
  status: ItemStatus;
  errors: ItemErrors;
  calculatedTotalItems: Totals | null;
}

export const initialState: CategoryState = {
  items: [],
  addItems: [],
  ids: [],
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 0,
    from: 0,
    links: [],
    path: "",
    to: 0,
  },
  status: {
    fetch: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
  },
  errors: {
    fetch: null,
    add: null,
    edit: null,
    delete: null,
  },
  calculatedTotalItems: null,
};

const itemsSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    clearItemState: (state) => {
      state = initialState;
      return state;
    },
    addItemsToState: (state, action: PayloadAction<ItemData[]>) => {
      state.addItems = action.payload.filter((item) => item.quantity > 0);
    },
    findAndReplaceAddedItem: (state, action: PayloadAction<ItemData>) => {
      const newItem = action.payload;
      const index = state.addItems.findIndex((item) => item.id === newItem.id);

      // If quantity is 0 → remove item
      if (newItem.quantity === 0) {
        if (index !== -1) {
          state.addItems.splice(index, 1);
        }
        return;
      }

      // If item exists → update
      if (index !== -1) {
        state.addItems[index] = newItem;
        return;
      }

      // If item does not exist → add
      state.addItems.push(newItem);
    },
    setCalculatedTotalItems: (state, action: PayloadAction<Totals>) => {
      state.calculatedTotalItems = action.payload;
    },
    clearCalculatedTotalItems: (state) => {
      state.calculatedTotalItems = null;
    },
  },
  extraReducers: (builder) => {
    // get Categories
    builder.addCase(getItems.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getItems.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<ItemData>>>,
      ) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.items = action.payload.payload.data;
        state.ids = action.payload.payload.data.map((item) => item.id);
        state.pagination = action.payload.payload.meta;
      },
    );
    builder.addCase(getItems.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    builder.addCase(addItem.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });

    builder.addCase(addItem.fulfilled, (state, action) => {
      state.status.add = "succeeded";
      const addedCategory = action.payload.payload;
      state.items.unshift(addedCategory);
      state.ids.unshift(addedCategory.id);
      if (state.items.length > state.pagination.per_page) {
        state.pagination.to--;
        state.items.pop();
      }
      state.addItems.push(addedCategory);
      state.pagination.to++;
      state.pagination.total++;
      state.pagination.last_page = Math.ceil(
        state.pagination.total / state.pagination.per_page,
      );
    });
    builder.addCase(addItem.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    //EDIT ITEM
    builder.addCase(editItem.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
    });
    builder.addCase(editItem.fulfilled, (state, action) => {
      state.status.edit = "succeeded";
      const updatedItem = action.payload.payload;
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      state.items[index] = updatedItem;
      const addItemIndex = state.addItems.findIndex(
        (item) => item.id === updatedItem.id,
      );
      if (addItemIndex !== -1) {
        state.addItems[addItemIndex] = updatedItem;
      } else {
        state.addItems.push(updatedItem);
      }
    });
    builder.addCase(editItem.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
    });

    // delete Category
    builder.addCase(deleteItem.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteItem.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedItemId = action.payload.payload;
        state.items = state.items.filter((item) => item.id !== deletedItemId);

        state.ids = state.ids.filter((id) => id !== deletedItemId);

        state.addItems = state.addItems.filter(
          (item) => item.id !== deletedItemId,
        );
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page,
        );
      },
    );
    builder.addCase(deleteItem.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });

    //Upload CSV
    builder.addCase(
      addItemFromCSV.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<ItemData>>>,
      ) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.items = action.payload.payload.data;
        state.ids = action.payload.payload.data.map((item) => item.id);
        state.pagination = action.payload.payload.meta;
      },
    );
    builder.addCase(addItemFromCSV.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    builder.addCase(addItemFromCSV.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
  },
});

export default itemsSlice.reducer;

export const {
  clearItemState,
  addItemsToState,
  findAndReplaceAddedItem,
  setCalculatedTotalItems,
} = itemsSlice.actions;

export const selectItemsState = (state: any) =>
  state.item as typeof initialState;
